import { useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/index';
import { useAtomValue } from 'jotai';

import type { ZuubOnboardingBeginResponse } from '@/@types/zuub/contracts';
import { InfoBanner } from '@/common-components/elements/InfoBanner';

import { useZuubRcmOnboardingBegin } from '../../../react-query/rcm/useZuubRcmOnboarding';
import { selectedPlanAtom } from '../atoms';
import { ConnectToPayerPortalDialog } from '../connectCredentials/ConnectToPayerPortalDialog';

import { PatientBenefitsLayout } from './PatientBenefitsLayout';
import { SectionCardWithHeader } from './sectionPages/common/SectionCardWithHeader';
import { CoverageDetailsSection } from './sectionPages/CoverageDetailsSection';
import { CoverageSummarySection } from './sectionPages/CoverageSummarySection';
import { LimitationsAndDisclaimersSection } from './sectionPages/limitationsAndDisclaimers/LimitationsAndDisclaimersSection';
import { MaximumsAndDeductiblesSection } from './sectionPages/maximumsAndDeductibles/MaximumsAndDeductiblesSection';
import { PatientInformationSection } from './sectionPages/PatientInformationSection';
import { PlanSummarySection } from './sectionPages/PlanSummarySection';
import { TreatmentHistorySection } from './sectionPages/treatmentHistory/TreatmentHistorySection';
import { WaitingPeriodsSection } from './sectionPages/waitingPeriods/WaitingPeriodsSection';

export const PatientBenefitsCombinedPage = () => {
  const currentPlan = useAtomValue(selectedPlanAtom);
  const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);
  const [onboardingResponse, setOnboardingResponse] =
    useState<ZuubOnboardingBeginResponse | null>(null);
  const [isLoadingOnboarding, setIsLoadingOnboarding] = useState(false);
  const [onboardingError, setOnboardingError] = useState<Error | null>(null);

  const { t } = useLingui();
  const beginOnboardingMutation = useZuubRcmOnboardingBegin();

  // Check if the currently selected plan has a plan type
  const hasPlanType = currentPlan?.plan?.planType != null;

  const handleSetUpCredentials = () => {
    const payerId = currentPlan?.payerId;
    if (!payerId) return;

    setIsLoadingOnboarding(true);
    setOnboardingError(null);
    setIsConnectDialogOpen(true);

    beginOnboardingMutation.mutate(
      { payerId },
      {
        onSuccess: (response) => {
          setOnboardingResponse(response);
          setIsLoadingOnboarding(false);
          setOnboardingError(null);
        },
        onError: (error) => {
          setIsLoadingOnboarding(false);
          setOnboardingError(error);
        },
      },
    );
  };

  return (
    <PatientBenefitsLayout>
      <div className="vai-space-y-8">
        {/* Incomplete Benefits Information Banner */}
        <InfoBanner
          title={t`Enable More Benefits Information`}
          description={t`Benefits information will be limited until you connect Videa to this payer portal. We'll help you get set up.`}
          variant="primary"
          actionButton={
            <Button
              size="sm"
              className="vai-bg-blue-600 hover:vai-bg-blue-700"
              onClick={handleSetUpCredentials}
            >
              <Trans>Set Up Credentials</Trans>
            </Button>
          }
          onClose={() => {
            // TODO: Handle close
          }}
        />

        {/* Plan Summary */}
        <PlanSummarySection />

        {/* Patient Information Section */}
        <div id="patient-information-section">
          <SectionCardWithHeader title={t`Patient Information`}>
            <PatientInformationSection />
          </SectionCardWithHeader>
        </div>

        {/* Coverage Summary Section - only show if selected plan has a plan type */}
        {hasPlanType && (
          <div id="coverage-summary-section">
            <SectionCardWithHeader title={t`Coverage Summary`}>
              <CoverageSummarySection />
            </SectionCardWithHeader>
          </div>
        )}

        {/* Coverage Details Section - only show if selected plan has a plan type */}
        {hasPlanType && (
          <div id="coverage-details">
            <SectionCardWithHeader title={t`Coverage Details`}>
              <CoverageDetailsSection />
            </SectionCardWithHeader>
          </div>
        )}

        {/* Maximums & Deductibles Section */}
        <div id="maximums-deductibles-section">
          <SectionCardWithHeader title={t`Maximums & Deductibles`}>
            <MaximumsAndDeductiblesSection />
          </SectionCardWithHeader>
        </div>

        {/* Limitations & Disclaimers Section */}
        <div id="limitations-and-disclaimers-section">
          <SectionCardWithHeader title={t`Limitations & Disclaimers`}>
            <LimitationsAndDisclaimersSection />
          </SectionCardWithHeader>
        </div>

        {/* Waiting Periods Section */}
        <div id="waiting-periods-section">
          <SectionCardWithHeader title={t`Waiting Periods`}>
            <WaitingPeriodsSection />
          </SectionCardWithHeader>
        </div>

        {/* Treatment History Section */}
        <div id="treatment-history-section">
          <SectionCardWithHeader title={t`Treatment History`}>
            <TreatmentHistorySection />
          </SectionCardWithHeader>
        </div>
      </div>

      {/* Connect to Payer Portal Dialog */}
      {currentPlan?.payerId && (
        <ConnectToPayerPortalDialog
          payerId={currentPlan.payerId}
          onboardingError={onboardingError}
          onboardingResponse={onboardingResponse}
          isLoadingOnboarding={isLoadingOnboarding}
          isOpen={isConnectDialogOpen}
          onCancel={() => {
            setIsConnectDialogOpen(false);
            setOnboardingResponse(null);
            setIsLoadingOnboarding(false);
            setOnboardingError(null);
          }}
          onIsOpenChange={(isOpen) => {
            setIsConnectDialogOpen(isOpen);
            if (!isOpen) {
              setOnboardingResponse(null);
              setIsLoadingOnboarding(false);
              setOnboardingError(null);
            }
          }}
        />
      )}
    </PatientBenefitsLayout>
  );
};
